import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Clock3, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const remainingLabel = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  const days = Math.floor(safe / 86400);
  const hours = Math.max(1, Math.ceil((safe % 86400) / 3600));
  return days > 0 ? `${days}d ${hours}h left` : `${hours}h left`;
};

export const TrashDialog = ({ open, onOpenChange, onRestored, onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/trash`);
      const next = data.items || [];
      setItems(next);
      onCountChange?.(data.count || 0);
    } catch (error) {
      toast.error("Failed to load Trash");
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    if (open) loadTrash();
  }, [open, loadTrash]);

  const restore = async (item) => {
    setRestoring(item.group_id);
    try {
      const { data } = await axios.post(`${API}/trash/${item.group_id}/restore`);
      const next = items.filter((entry) => entry.group_id !== item.group_id);
      setItems(next);
      onCountChange?.(next.length);
      onRestored?.(item);
      toast.success(`${data.restored} record${data.restored === 1 ? "" : "s"} restored`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to restore record");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trash-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="trash-dialog-title">
            <Trash2 className="h-5 w-5 text-rose-500" /> Trash
          </DialogTitle>
          <DialogDescription data-testid="trash-dialog-description">
            Deleted Data Ledger records can be restored for 3 days, then they are permanently removed.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1" data-testid="trash-items-list">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-400" data-testid="trash-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading Trash…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="trash-empty-state">
              <Trash2 className="mx-auto h-9 w-9 text-slate-200" />
              <p className="mt-3 text-sm font-semibold text-slate-600">Trash is empty</p>
              <p className="mt-1 text-xs text-slate-400">Deleted Data Ledger records will appear here.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {items.map((item) => (
                <div key={item.group_id} data-testid={`trash-item-${item.group_id}`}
                  className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-slate-900" data-testid={`trash-report-${item.group_id}`}>
                        {item.report_name}
                      </span>
                      {item.report_month && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                          {item.report_month}
                        </span>
                      )}
                      {item.linked_count > 0 && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700"
                          data-testid={`trash-linked-count-${item.group_id}`}>
                          +{item.linked_count} linked
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate text-sm text-slate-600" title={item.preview}
                      data-testid={`trash-preview-${item.group_id}`}>{item.preview}</p>
                    <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-rose-500"
                      data-testid={`trash-expiry-${item.group_id}`}>
                      <Clock3 className="h-3 w-3" /> {remainingLabel(item.expires_in_seconds)}
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" disabled={restoring === item.group_id}
                    onClick={() => restore(item)} data-testid={`restore-trash-${item.group_id}`}
                    className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800">
                    {restoring === item.group_id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1.5 h-4 w-4" />}
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};